/**
 * 展开状态管理 Hook
 * 处理树节点的展开/收起状态
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import React from 'react';
import { isInclude, loopAllChildren } from '../utils/util';

interface UseExpandStateProps {
    defaultExpandedKeys?: string[];
    expandedKeys?: string[];
    defaultExpandAll?: boolean;
    autoExpandParent?: boolean;
    onExpand?: (expandedKeys: string[], info: { node: React.ReactElement; expanded: boolean }, e?: React.MouseEvent | React.KeyboardEvent) => void;
    children?: React.ReactNode;
    treeData?: any[];
    canCloseFreely?: boolean;
    loadData?: (treeNode: React.ReactElement) => Promise<any>;
    onLoad?: (loadedKeys: string[], info: { event: string; node: React.ReactElement }) => void;
    fieldNames?: any;
    controlled?: boolean;
}

interface UseExpandStateReturn {
    expandedKeys: string[];
    loadedKeys: string[];
    cacheExpandedKeys: Set<string> | null;
    autoExpandKeys: string[] | null;
    handleExpand: (treeNode: React.ReactElement, keyType?: string, e?: React.MouseEvent | React.KeyboardEvent) => Promise<any> | undefined;
    setExpandedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    setLoadedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    getFilterExpandedKeys: (props: any, expandKeyProp: string, expandAll: boolean) => string[];
    getRawExpandedKeys: () => string[] | undefined;
    getExpandedKeys: (treeNode: React.ReactElement, expand: boolean) => string[] | undefined;
    isFold: boolean;
}

/**
 * 展开状态管理 Hook
 */
export function useExpandState(props: UseExpandStateProps): UseExpandStateReturn {
    const {
        defaultExpandedKeys = [],
        expandedKeys: controlledExpandedKeys,
        controlled,
        defaultExpandAll = false,
        autoExpandParent = true,
        onExpand,
        children,
        treeData,
        canCloseFreely,
        loadData,
        onLoad,
        fieldNames
    } = props;

    // refs
    const cacheExpandedKeysRef = useRef<Set<string> | null>(null);
    const autoExpandKeysRef = useRef<string[] | null>(null);
    const rawExpandedKeysRef = useRef<string[] | undefined>(undefined);
    const latestStateRef = useRef<boolean | null>(null);
    const latestTreeNodeRef = useRef<any>({});
    const cachedLatestStateRef = useRef<boolean | null>(null);

    // 提前定义 getFilterExpandedKeys 函数，避免循环依赖
    function getFilterExpandedKeysInternal(props: any, expandKeyProp: string, expandAll: boolean): string[] {
        let keys: string[] = props[expandKeyProp] || [];

        // 如果默认展开值数组有值的话，这些值不作为展开值
        if (expandKeyProp === 'defaultExpandedKeys' && autoExpandKeysRef.current && autoExpandKeysRef.current.length) {
            keys = [];
        }

        if (!expandAll && !autoExpandParent) {
            return keys || [];
        }

        let childs = children;
        if (treeData && !childs) {
            // 需要从treeData渲染children，传递正确的fieldNames
            childs = renderTreefromData(treeData, fieldNames);
            // console.log('renderTreefromData result:', childs?.length, 'treeData:', treeData);
        }

        const expandedPositionArr: string[] = [];
        if (autoExpandParent) {
            loopAllChildren(childs as React.ReactElement[], (_item: React.ReactElement, _index: number, pos: string, newKey: string) => {
                if (keys.indexOf(newKey) > -1) {
                    expandedPositionArr.push(pos);
                }
            });
        }

        const filterExpandedKeys: string[] = [];
        const removeRoot = latestStateRef.current === false && canCloseFreely;
        const cachedLatestState = latestStateRef.current;
        latestStateRef.current = null;

        loopAllChildren(childs as React.ReactElement[], (_item: React.ReactElement, _index: number, pos: string, newKey: string) => {
            if (expandAll) {
                filterExpandedKeys.push(newKey);
            } else if (autoExpandParent) {
                // 对于所有情况，检查是否需要展开父节点
                let shouldExpand = false;

                // 如果节点本身在 keys 中，应该展开
                if (keys.includes(newKey)) {
                    shouldExpand = true;
                }

                // 检查是否是某个展开节点的父节点
                if (!shouldExpand) {
                    expandedPositionArr.forEach(p => {
                        if ((p.split('-').length > pos.split('-').length && isInclude(pos.split('-'), p.split('-')) || pos === p) && filterExpandedKeys.indexOf(newKey) === -1) {
                            shouldExpand = true;
                        }
                    });
                }

                if (shouldExpand) {
                    if (!canCloseFreely || (cachedLatestState === null ? true : cacheExpandedKeysRef.current ? cacheExpandedKeysRef.current.has(newKey) : true)) {
                        filterExpandedKeys.push(newKey);
                    }
                }
            }
        });

        if (removeRoot && latestTreeNodeRef.current.eventKey && filterExpandedKeys.includes(latestTreeNodeRef.current.eventKey)) {
            const index = filterExpandedKeys.indexOf(latestTreeNodeRef.current.eventKey);
            filterExpandedKeys.splice(index, 1);
        }

        return filterExpandedKeys.length ? filterExpandedKeys : keys;
    }

    /**
     * 获取默认展开的keys
     */
    function getDefaultExpandedKeys(props: UseExpandStateProps): string[] {
        if (controlled && props.expandedKeys) {
            const result = autoExpandParent
                ? getFilterExpandedKeysInternal(props, 'expandedKeys', false)
                : props.expandedKeys;
            // console.log('[useExpandState] getDefaultExpandedKeys - input:', props.expandedKeys, 'output:', result, 'autoExpandParent:', autoExpandParent);
            return result;
        }

        return getFilterExpandedKeysInternal(
            props,
            'defaultExpandedKeys',
            defaultExpandedKeys.length ? false : defaultExpandAll
        );
    }

    // 状态管理
    const [expandedKeys, setExpandedKeys] = useState<string[]>(() => {
        if (defaultExpandedKeys && Array.isArray(defaultExpandedKeys) && defaultExpandedKeys.length && loadData) {
            autoExpandKeysRef.current = defaultExpandedKeys;
        }
        const initialKeys = getDefaultExpandedKeys(props);
        // 初始化 cacheExpandedKeys
        cacheExpandedKeysRef.current = new Set(initialKeys);
        return initialKeys;
    });
    const [loadedKeys, setLoadedKeys] = useState<string[]>([]);

    // // 如果是异步加载数据，而且传了默认展开的节点key
    // useEffect(() => {

    // }, []);

    // 同步受控的 expandedKeys
    useEffect(() => {
        if (controlledExpandedKeys !== undefined) {
            // 如果启用了 autoExpandParent，需要计算包含父节点的完整 expandedKeys
            const finalKeys = autoExpandParent
                ? getFilterExpandedKeysInternal({ ...props, expandedKeys: controlledExpandedKeys }, 'expandedKeys', false)
                : controlledExpandedKeys;
            setExpandedKeys(finalKeys);
            cacheExpandedKeysRef.current = new Set(finalKeys);
        }
    }, [controlledExpandedKeys, autoExpandParent]);

    // 同步 expandedKeys 变化到 cacheExpandedKeys
    useEffect(() => {
        cacheExpandedKeysRef.current = new Set(expandedKeys);
    }, [expandedKeys]);

    /**
     * 获取过滤后的展开keys（用于返回的接口）
     */
    const getFilterExpandedKeys = useCallback((props: any, expandKeyProp: string, expandAll: boolean): string[] => {
        return getFilterExpandedKeysInternal(props, expandKeyProp, expandAll);
    }, [children, treeData, autoExpandParent, canCloseFreely, fieldNames]);

    /**
     * 处理节点展开/收起
     */
    const handleExpand = useCallback((
        treeNode: React.ReactElement,
        keyType?: string,
        e?: React.MouseEvent | React.KeyboardEvent
    ): Promise<any> | undefined => {
        let expanded = !treeNode.props.expanded;
        const isRootNode = checkIsRootNode(treeNode, children);

        latestStateRef.current = isRootNode ? expanded : null;
        cachedLatestStateRef.current = treeNode.props.expanded;
        latestTreeNodeRef.current = treeNode.props;

        const newExpandedKeys = [...expandedKeys];
        const index = newExpandedKeys.indexOf(treeNode.props.eventKey);

        // 如果是autoExpand触发的展开，从autoExpandKeys中移除
        if (autoExpandKeysRef.current && autoExpandKeysRef.current.includes(treeNode.props.eventKey)) {
            const autoIndex = autoExpandKeysRef.current.indexOf(treeNode.props.eventKey);
            autoExpandKeysRef.current.splice(autoIndex, 1);
            if (autoExpandKeysRef.current.length === 0) {
                autoExpandKeysRef.current = null;
            }
        }

        if (keyType === 'left') {
            expanded = false;
        } else if (keyType === 'right') {
            expanded = true;
        }

        if (expanded && index === -1) {
            newExpandedKeys.push(treeNode.props.eventKey);
        } else if (!expanded && index > -1) {
            newExpandedKeys.splice(index, 1);
        }

        if (!controlled) {
            setExpandedKeys(newExpandedKeys);
        }

        onExpand?.(newExpandedKeys, { node: treeNode, expanded }, e);

        // 缓存 expandedKeys
        cacheExpandedKeysRef.current = new Set(newExpandedKeys);

        // 处理异步加载
        if (expanded && loadData) {
            if (loadedKeys.includes(treeNode.props.eventKey) && treeNode.props.children?.length) {
                return;
            }

            return loadData(treeNode)?.then(() => {
                if (!controlled) {
                    const allExpandedKeys = Array.from(new Set([...expandedKeys, ...newExpandedKeys]));
                    setExpandedKeys(allExpandedKeys);
                }

                const allLoadedKeys = Array.from(new Set([...loadedKeys, treeNode.props.eventKey]));
                setLoadedKeys(allLoadedKeys);

                onLoad?.(allLoadedKeys, {
                    event: 'load',
                    node: treeNode
                });
            });
        }
    }, [expandedKeys, loadedKeys, props, onExpand, loadData, onLoad, children]);

    /**
     * 获取原始展开keys
     */
    const getRawExpandedKeys = useCallback((): string[] | undefined => {
        if (!rawExpandedKeysRef.current && controlled) {
            rawExpandedKeysRef.current = [...expandedKeys];
        }
        return rawExpandedKeysRef.current;
    }, [expandedKeys, props]);

    /**
     * 获取展开的keys
     */
    const getExpandedKeys = useCallback((treeNode: React.ReactElement, expand: boolean): string[] | undefined => {
        const key = treeNode.props.eventKey;
        const expandedIndex = expandedKeys.indexOf(key);
        let exKeys;

        if (expandedIndex > -1 && !expand) {
            exKeys = [...expandedKeys];
            exKeys.splice(expandedIndex, 1);
            return exKeys;
        }

        if (expand && expandedKeys.indexOf(key) === -1) {
            return expandedKeys.concat([key]);
        }
    }, [expandedKeys]);

    return {
        expandedKeys,
        loadedKeys,
        cacheExpandedKeys: cacheExpandedKeysRef.current,
        autoExpandKeys: autoExpandKeysRef.current,
        handleExpand,
        setExpandedKeys,
        setLoadedKeys,
        getFilterExpandedKeys,
        getRawExpandedKeys,
        getExpandedKeys,
        isFold: !!cachedLatestStateRef.current
    };
}

// 辅助函数
function checkIsRootNode(node: React.ReactElement, children: React.ReactNode): boolean {
    if (!children) return false;
    const childArray = React.Children.toArray(children);
    return childArray.length === 1 && (childArray[0] as React.ReactElement).key === node.props.eventKey;
}

/**
 * 从 treeData 渲染虚拟的 ReactElement 树
 * 用于在懒加载模式下处理 autoExpandParent 逻辑
 */
function renderTreefromData(data: any[], fieldNames?: any): React.ReactElement[] {
    if (!data || !Array.isArray(data)) {
        return [];
    }

    const TreeNode = require('../TreeNode').default;

    const loop = (data: any[]): React.ReactElement[] => data.map((item) => {
        let { key, title, isLeaf, children, ...others } = item;

        // 处理 fieldNames
        if (fieldNames) {
            title = fieldNames.title ? item[fieldNames.title] : title;
            key = fieldNames.key ? item[fieldNames.key] : key;
            children = fieldNames.children ? item[fieldNames.children] : children;
        }

        key = key + "";

        if (children && children.length > 0) {
            return React.createElement(
                TreeNode,
                {
                    key,
                    title,
                    isLeaf,
                    ...others
                },
                loop(children)
            );
        }

        return React.createElement(
            TreeNode,
            {
                key,
                title,
                isLeaf: isLeaf !== false,
                ...others
            }
        );
    });

    return loop(data);
}

